import { useState, type ReactNode } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type {
  ScreenCatalog,
  ScreenGroup,
  ScreenItem,
} from "../../shared/api/platformBilling";

/**
 * Khung duyệt danh mục tính năng (ADR-0053), dùng chung cho bảng tick của GÓI và bảng bật/tắt
 * của một PHÒNG KHÁM: hai màn hình đó khác nhau ở từng dòng (tick nháp rồi lưu một lượt, hay gạt
 * là lưu ngay kèm lý do) nhưng giống nhau ở cách tìm một tính năng giữa hàng chục tính năng trong
 * hơn mười nhóm. Khung trái là ô tìm kiếm, vài bộ lọc do bên gọi khai, rồi danh sách nhóm kèm
 * đếm; khung phải chỉ hiện lựa chọn đang chọn. Ô tìm kiếm tìm trên toàn bộ danh mục.
 *
 * <p>Khung này không biết dòng là gì: `S` là trạng thái bên gọi gắn cho từng màn hình, và `isOn`
 * chỉ để đếm "bật/tổng" của mỗi nhóm. Màn hình có trong danh mục nhưng không có trạng thái thì
 * không hiện — danh mục và trạng thái là hai lần gọi, có thể lệch nhau một màn hình mới.
 */
export interface FeatureEntry<S> {
  screen: ScreenItem;
  state: S;
  groupCode: string;
  groupLabel: string;
}

/** Một mục lọc ở khung trái, gom tính năng của mọi nhóm thoả `match`. */
export interface FeatureFilter<S> {
  key: string;
  /** Nhãn ngắn ở khung trái. */
  label: string;
  /** Tiêu đề đầy đủ ở khung phải. */
  title: string;
  /** Tô đếm màu vàng khi có ít nhất một dòng: thứ cần người xem để mắt. */
  tone?: "amber";
  match: (entry: FeatureEntry<S>) => boolean;
}

type View = `filter:${string}` | `group:${string}`;

export function FeatureBrowser<S>({
  catalog,
  stateOf,
  isOn,
  filters,
  hint,
  layout = "list",
  onNavigate,
  renderGroupTools,
  renderRow,
}: {
  catalog: ScreenCatalog;
  stateOf: (code: string) => S | undefined;
  isOn: (state: S) => boolean;
  filters: FeatureFilter<S>[];
  /** Một câu dưới tiêu đề khung phải, nói công tắc/ô tick nghĩa là gì. */
  hint: string;
  /**
   * `list`: mỗi dòng một hàng ngang, cho dòng có thao tác bên phải (công tắc, ô lý do). `grid`:
   * nhiều cột, cho dòng chỉ là một ô tick và tên — tận dụng bề ngang thay vì một cột dài.
   */
  layout?: "list" | "grid";
  /** Đổi nhóm/bộ lọc/tìm kiếm: dòng đang mở gì đó sắp khuất, bên gọi có thể muốn đóng nó. */
  onNavigate?: () => void;
  /** Thao tác cho cả nhóm, hiện ở đầu khung phải khi đang xem một nhóm. */
  renderGroupTools?: (
    group: ScreenGroup,
    entries: FeatureEntry<S>[],
  ) => ReactNode;
  /** `groupLabel` chỉ truyền khi danh sách trộn nhiều nhóm (bộ lọc, tìm kiếm). */
  renderRow: (entry: FeatureEntry<S>, groupLabel?: string) => ReactNode;
}) {
  const [view, setView] = useState<View>(
    `group:${catalog.groups[0]?.code ?? ""}`,
  );
  const [query, setQuery] = useState("");

  const entries: FeatureEntry<S>[] = catalog.groups.flatMap((g) =>
    g.screens.flatMap((screen) => {
      const state = stateOf(screen.code);
      return state === undefined
        ? []
        : [{ screen, state, groupCode: g.code, groupLabel: g.label }];
    }),
  );
  const ofGroup = (code: string) => entries.filter((e) => e.groupCode === code);

  const q = query.trim().toLocaleLowerCase("vi");
  const currentGroup = catalog.groups.find((g) => `group:${g.code}` === view);
  const currentFilter = filters.find((f) => `filter:${f.key}` === view);
  const listing = q
    ? {
        title: `Kết quả cho “${query.trim()}”`,
        entries: entries.filter((e) =>
          e.screen.label.toLocaleLowerCase("vi").includes(q),
        ),
        mixed: true,
      }
    : currentFilter
      ? {
          title: currentFilter.title,
          entries: entries.filter(currentFilter.match),
          mixed: true,
        }
      : {
          title: currentGroup?.label ?? "",
          entries: currentGroup ? ofGroup(currentGroup.code) : [],
          mixed: false,
        };

  const pick = (v: View) => {
    onNavigate?.();
    setQuery("");
    setView(v);
  };
  const isActive = (v: View) => !q && v === view;

  return (
    <div className="grid min-h-0 flex-1 overflow-hidden rounded-lg border md:grid-cols-[15rem_1fr]">
      <nav
        aria-label="Nhóm tính năng"
        className="max-h-48 overflow-y-auto border-b bg-muted/30 p-2 md:max-h-none md:border-b-0 md:border-r"
      >
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => {
              onNavigate?.();
              setQuery(e.target.value);
            }}
            placeholder="Tìm tính năng"
            aria-label="Tìm tính năng"
            className="h-9 pl-8"
          />
        </div>
        {filters.map((f) => {
          const n = entries.filter(f.match).length;
          return (
            <NavItem
              key={f.key}
              label={f.label}
              count={n}
              tone={n > 0 ? f.tone : undefined}
              active={isActive(`filter:${f.key}`)}
              onClick={() => pick(`filter:${f.key}`)}
            />
          );
        })}
        <p className="mb-1 mt-3 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Nhóm
        </p>
        {catalog.groups.map((g) => {
          const inGroup = ofGroup(g.code);
          if (inGroup.length === 0) return null;
          return (
            <NavItem
              key={g.code}
              label={g.label}
              count={`${inGroup.filter((e) => isOn(e.state)).length}/${inGroup.length}`}
              active={isActive(`group:${g.code}`)}
              onClick={() => pick(`group:${g.code}`)}
            />
          );
        })}
      </nav>

      <div className="flex min-h-0 flex-col">
        <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
          <div className="min-w-0">
            <h3 className="font-semibold">{listing.title}</h3>
            <p className="text-xs text-muted-foreground">{hint}</p>
          </div>
          {!listing.mixed &&
            currentGroup &&
            renderGroupTools?.(currentGroup, listing.entries)}
        </div>
        {listing.entries.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">
            {q
              ? "Không có tính năng nào khớp."
              : "Không có tính năng nào ở đây."}
          </p>
        ) : (
          <ul
            className={cn(
              "flex-1 overflow-y-auto",
              layout === "grid"
                ? "grid content-start gap-x-4 gap-y-1 p-3 sm:grid-cols-2 lg:grid-cols-3"
                : "divide-y",
            )}
          >
            {listing.entries.map((e) =>
              renderRow(e, listing.mixed ? e.groupLabel : undefined),
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function NavItem({
  label,
  count,
  tone,
  active,
  onClick,
}: {
  label: string;
  count: number | string;
  tone?: "amber";
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? "true" : undefined}
      onClick={onClick}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent",
        active && "bg-accent font-medium",
      )}
    >
      <span className="truncate">{label}</span>
      <span
        className={cn(
          "shrink-0 text-xs tabular-nums",
          tone === "amber"
            ? "font-medium text-amber-600"
            : "text-muted-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}

/** Tên tính năng kèm tên nhóm khi danh sách đang trộn nhiều nhóm. Dùng chung cho mọi dòng. */
export function FeatureName({
  label,
  groupLabel,
}: {
  label: string;
  groupLabel?: string;
}) {
  return (
    <p className="text-sm font-medium">
      {label}
      {groupLabel && (
        <span className="ml-2 text-xs font-normal text-muted-foreground">
          {groupLabel}
        </span>
      )}
    </p>
  );
}
